"use client";

import Link from "next/link";
import { useState } from "react";
import { usePathname } from "next/navigation";

/**
 * Sitewide Halloween Banner - Appears at the top of all pages
 * Displays Halloween special offer with dismiss functionality
 *
 * Behavior:
 * - Desktop: Hidden on pricing page
 * - Mobile: Shows on all pages including pricing
 */
export default function HalloweenBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const pathname = usePathname();

  // Hide on pricing page for desktop only
  const isPricingPage = pathname === "/pricing";

  if (!isVisible) return null;

  return (
    <div className={`relative bg-gradient-to-r from-orange-600 to-purple-700 sm:from-orange-600 sm:via-purple-700 sm:to-orange-600 text-white py-3 px-4 shadow-lg z-50 ${
      isPricingPage ? "md:hidden" : ""
    }`}>
      {/* Halloween emojis background */}
      <div className="absolute inset-0 overflow-hidden opacity-20 pointer-events-none">
        <div className="absolute top-2 left-[5%] text-3xl">🎃</div>
        <div className="absolute top-2 left-[19%] text-2xl">👻</div>
        <div className="absolute top-2 left-1/2 text-3xl">🎃</div>
        <div className="absolute top-2 left-[77%] text-2xl">🦇</div>
        <div className="absolute top-2 left-[95%] text-3xl">🎃</div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex flex-row items-center justify-center gap-2 sm:gap-4 text-center">
          {/* Mobile layout */}
          <div className="flex items-center gap-1.5 sm:hidden">
            <span className="text-xl">🎃</span>
            <div className="flex items-center gap-1.5">
              <span className="font-black text-sm">LOCK IN</span>
              <span className="font-bold text-sm">27% OFF FOREVER!</span>
            </div>
            <span className="text-xl">👻</span>
          </div>

          {/* Desktop layout */}
          <div className="hidden sm:flex items-center gap-3">
            <span className="text-2xl">🎃</span>
            <span className="font-bold text-md tracking-wide">HALLOWEEN SPECIAL</span>
            <span className="text-orange-200 text-base">•</span>
            <span className=" text-base">Lock in 27% OFF Standard Plan Forever - Limited Time!</span>
            <span className="text-2xl">👻</span>
          </div>

          {/* CTA Button */}
          <Link
            href="/pricing"
            className="bg-white text-orange-600 px-3 sm:px-6 py-1 sm:py-2 rounded-full font-bold text-xs sm:text-base hover:bg-orange-50 shadow-lg whitespace-nowrap"
          >
            Get Deal 🎃
          </Link>
        </div>

        {/* Close button - left on mobile, right on desktop */}
        <button
          onClick={() => setIsVisible(false)}
          className="absolute left-0 sm:left-auto sm:right-0 top-1/2 -translate-y-1/2 p-1 hover:bg-white/20 rounded-full"
          aria-label="Close banner"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

